import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import MathText from '../components/MathText';
import {
    FiBookOpen,
    FiCheck,
    FiChevronLeft,
    FiCompass,
    FiMessageCircle,
    FiPlus,
    FiRefreshCw,
    FiSend,
    FiTarget,
    FiTrash2,
    FiTrendingUp,
} from 'react-icons/fi';
import {getErrorMessage, learningAgentApi} from '../services/api';
import './LearningAgent.css';

const levelLabels = {
    unassessed: 'هنوز ارزیابی نشده',
    foundation: 'سطح پایه',
    developing: 'در حال توسعه',
    proficient: 'مسلط',
    advanced: 'پیشرفته',
};

const reasonLabels = {
    prerequisite_gap: 'نیاز به تکمیل پیش‌نیاز',
    target: 'هدف نهایی',
    prerequisite: 'پیش‌نیاز هدف',
    below_threshold: 'نیازمند تمرین بیشتر',
    prerequisites_ready: 'آماده‌ی یادگیری',
};

const messageText = (message) => {
    if (typeof message?.content === 'string') return message.content;
    if (typeof message?.text === 'string') return message.text;
    if (Array.isArray(message?.content)) {
        return message.content.map((item) => item?.text || '').filter(Boolean).join('\n');
    }
    return '';
};

const percent = (value) => value == null ? '—' : `${Math.round(value * 100)}٪`;

const LearningAgent = () => {
    const [conversations, setConversations] = useState([]);
    const [active, setActive] = useState(null);
    const [graphs, setGraphs] = useState([]);
    const [profile, setProfile] = useState(null);
    const [mastery, setMastery] = useState([]);
    const [messages, setMessages] = useState([]);
    const [message, setMessage] = useState('');
    const [goal, setGoal] = useState('');
    const [pendingGoal, setPendingGoal] = useState(null);
    const [learningPath, setLearningPath] = useState([]);
    const [activities, setActivities] = useState([]);
    const [material, setMaterial] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [working, setWorking] = useState('');
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const chatEndRef = useRef(null);

    const visibleMessages = useMemo(() => messages
        .filter((item) => ['user', 'assistant'].includes(item?.role) && messageText(item))
        .map((item, index) => ({...item, key: item.id || `${item.role}-${index}`, text: messageText(item)})), [messages]);

    const loadLearningData = useCallback(async (conversation) => {
        setActive(conversation);
        setMessages(conversation.messages || []);
        setProfile(null);
        setMastery([]);
        setLearningPath([]);
        setActivities([]);
        setMaterial(null);
        setPendingGoal(null);
        if (!conversation.graph_id) return;
        const [profileResult, masteryResult] = await Promise.all([
            learningAgentApi.invokeTool(conversation.id, 'get_my_learning_profile', {graph_id: conversation.graph_id}),
            learningAgentApi.invokeTool(conversation.id, 'get_my_concept_mastery', {
                graph_id: conversation.graph_id,
                include_unassessed: true,
                offset: 0,
                limit: 200,
            }),
        ]);
        setProfile(profileResult);
        setMastery(masteryResult.concepts || []);
    }, []);

    const initialize = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            let items = await learningAgentApi.conversations();
            if (!items.length) {
                items = [await learningAgentApi.createConversation({title: 'دستیار یادگیری من'})];
            }
            setConversations(items);
            const preferred = items.find((item) => item.graph_id) || items[0];
            const graphResult = await learningAgentApi.invokeTool(preferred.id, 'list_my_learning_graphs', {});
            setGraphs(graphResult.graphs || []);
            await loadLearningData(preferred);
        } catch (requestError) {
            setError(getErrorMessage(requestError));
        } finally {
            setLoading(false);
        }
    }, [loadLearningData]);

    useEffect(() => {
        initialize();
    }, [initialize]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({behavior: 'smooth'});
    }, [visibleMessages.length, sending]);

    const chooseGraph = async (event) => {
        const graphId = Number(event.target.value);
        if (!graphId) return;
        setWorking('graph');
        setError('');
        try {
            let conversation = conversations.find((item) => item.graph_id === graphId);
            if (!conversation) {
                const graph = graphs.find((item) => item.graph_id === graphId);
                conversation = await learningAgentApi.createConversation({
                    graph_id: graphId,
                    title: graph?.document_name || 'گفت‌وگوی یادگیری',
                });
                setConversations((current) => [conversation, ...current]);
            } else {
                conversation = await learningAgentApi.conversation(conversation.id);
            }
            await loadLearningData(conversation);
        } catch (requestError) {
            setError(getErrorMessage(requestError));
        } finally {
            setWorking('');
        }
    };

    const switchConversation = async (event) => {
        const selected = conversations.find((item) => item.id === event.target.value);
        if (!selected) return;
        setWorking('conversation');
        setError('');
        try {
            await loadLearningData(await learningAgentApi.conversation(selected.id));
        } catch (requestError) {
            setError(getErrorMessage(requestError));
        } finally {
            setWorking('');
        }
    };

    const createConversation = async () => {
        if (!active?.graph_id) return;
        setWorking('conversation');
        setError('');
        try {
            const created = await learningAgentApi.createConversation({
                graph_id: active.graph_id,
                title: 'گفت‌وگوی جدید',
            });
            setConversations((current) => [created, ...current]);
            await loadLearningData(created);
        } catch (requestError) {
            setError(getErrorMessage(requestError));
        } finally {
            setWorking('');
        }
    };

    const removeConversation = async () => {
        if (!active || !window.confirm('این گفت‌وگو حذف شود؟ سوابق تسلط شما حذف نخواهد شد.')) return;
        setWorking('conversation');
        setError('');
        try {
            await learningAgentApi.removeConversation(active.id);
            const remaining = conversations.filter((item) => item.id !== active.id);
            setConversations(remaining);
            if (remaining.length) await loadLearningData(remaining[0]);
            else {
                const created = await learningAgentApi.createConversation({title: 'دستیار یادگیری من'});
                setConversations([created]);
                await loadLearningData(created);
            }
        } catch (requestError) {
            setError(getErrorMessage(requestError));
        } finally {
            setWorking('');
        }
    };

    const sendMessage = async (event) => {
        event.preventDefault();
        const text = message.trim();
        if (!text || !active || sending) return;
        setMessage('');
        setSending(true);
        setError('');
        setMessages((current) => [...current, {role: 'user', content: text}]);
        try {
            const result = await learningAgentApi.chat(active.id, text);
            setMessages((current) => [...current, {role: 'assistant', content: result.answer}]);
            setActive((current) => current ? {...current, revision: result.revision} : current);
            const prepared = (result.tool_calls || [])
                .find((call) => call.name === 'prepare_learning_goal' && call.result?.confirmation_token)?.result;
            if (prepared) setPendingGoal(prepared);
        } catch (requestError) {
            setMessages((current) => current.filter((item, index) => !(index === current.length - 1 && item.role === 'user' && item.content === text)));
            setMessage(text);
            setError(getErrorMessage(requestError));
        } finally {
            setSending(false);
        }
    };

    const prepareGoal = async (goalText = goal) => {
        const text = goalText.trim();
        if (!text || !active?.graph_id) return;
        setWorking('goal');
        setError('');
        setNotice('');
        try {
            const result = await learningAgentApi.invokeTool(active.id, 'prepare_learning_goal', {
                graph_id: active.graph_id,
                goal: text,
                top_k: 5,
            });
            setGoal(text);
            setPendingGoal(result);
        } catch (requestError) {
            setError(getErrorMessage(requestError));
        } finally {
            setWorking('');
        }
    };

    const confirmGoal = async () => {
        if (!pendingGoal?.confirmation_token) return;
        setWorking('confirm');
        setError('');
        try {
            const confirmed = await learningAgentApi.confirmGoal(active.id, pendingGoal.confirmation_token);
            const args = {graph_id: active.graph_id, target: confirmed.target_concept_id};
            const [pathResult, activityResult] = await Promise.all([
                learningAgentApi.invokeTool(active.id, 'plan_learning_path', {...args, mastery_threshold: 0.7, max_depth: 10}),
                learningAgentApi.invokeTool(active.id, 'recommend_next_activities', {
                    ...args,
                    limit: 5,
                    mastery_threshold: 0.7,
                    readiness_threshold: 0.45,
                }),
            ]);
            setLearningPath(pathResult.steps || []);
            setActivities(activityResult.activities || []);
            setPendingGoal(null);
            setNotice(`هدف «${confirmed.target_concept_name}» ثبت شد.`);
        } catch (requestError) {
            setError(getErrorMessage(requestError));
        } finally {
            setWorking('');
        }
    };

    const showMaterial = async (conceptId) => {
        setWorking(`material-${conceptId}`);
        setError('');
        try {
            setMaterial(await learningAgentApi.invokeTool(active.id, 'get_concept_learning_material', {
                graph_id: active.graph_id,
                concept_id: conceptId,
            }));
        } catch (requestError) {
            setError(getErrorMessage(requestError));
        } finally {
            setWorking('');
        }
    };

    if (loading) return <div className="la-loading"><FiRefreshCw className="spin"/><span>در حال آماده‌سازی دستیار یادگیری…</span></div>;

    return (
        <div className="la-page">
            <header className="la-header app-page-header app-page-header--topbar">
                <div><span>یادگیری شخصی‌سازی‌شده</span><h1><FiCompass/> دستیار یادگیری</h1></div>
                <button type="button" className="la-refresh" onClick={initialize}><FiRefreshCw/> بروزرسانی</button>
            </header>

            <main className="la-content">
                {error && <div className="api-message error">{error}</div>}
                {notice && <div className="api-message success">{notice}</div>}

                <section className="la-toolbar">
                    <label><span>درس یا منبع</span><select aria-label="انتخاب درس یا منبع" value={active?.graph_id || ''} onChange={chooseGraph} disabled={!!working}>
                        <option value="">یک درس را انتخاب کنید</option>
                        {graphs.map((graph) => <option key={graph.graph_id} value={graph.graph_id}>{graph.document_name}</option>)}
                    </select></label>
                    <label><span>گفت‌وگو</span><select aria-label="انتخاب گفت‌وگوی یادگیری" value={active?.id || ''} onChange={switchConversation} disabled={!!working}>
                        {conversations.filter((item) => !active?.graph_id || item.graph_id === active.graph_id).map((item) => (
                            <option key={item.id} value={item.id}>{item.title || 'بدون عنوان'}</option>
                        ))}
                    </select></label>
                    <div className="la-toolbar-actions">
                        <button type="button" onClick={createConversation} disabled={!active?.graph_id || !!working}><FiPlus/> جدید</button>
                        <button type="button" className="danger" aria-label="حذف گفت‌وگوی فعلی" onClick={removeConversation} disabled={!active || !!working}><FiTrash2/></button>
                    </div>
                </section>

                {!graphs.length && <section className="la-empty"><FiBookOpen/><h2>هنوز گراف یادگیری آماده‌ای ندارید</h2><p>بعد از آماده‌شدن منبع آموزشی، درس‌های قابل استفاده اینجا نمایش داده می‌شوند.</p></section>}

                {!!graphs.length && !active?.graph_id && <section className="la-empty compact"><FiCompass/><h2>برای شروع یک درس انتخاب کنید</h2><p>پروفایل علمی و پیشنهادهای شما بر اساس همان درس نمایش داده می‌شود.</p></section>}

                {active?.graph_id && <>
                    <section className="la-overview">
                        <article className="la-profile-card">
                            <div className="la-card-title"><FiTrendingUp/><div><span>وضعیت فعلی</span><h2>{levelLabels[profile?.overall_level] || 'در حال دریافت'}</h2></div></div>
                            <div className="la-score"><strong>{percent(profile?.mastery_score)}</strong><span>تسلط کلی</span></div>
                            <div className="la-confidence"><span>اطمینان داده‌ها</span><b>{percent(profile?.confidence)}</b></div>
                            {profile?.overall_level === 'unassessed' && <p className="la-muted">برای برآورد سطح، ابتدا چند فعالیت یا آزمون انجام دهید.</p>}
                        </article>

                        <article className="la-goal-card">
                            <div className="la-card-title"><FiTarget/><div><span>هدف یادگیری</span><h2>می‌خواهید به کجا برسید؟</h2></div></div>
                            <div className="la-goal-form">
                                <input aria-label="هدف یادگیری" value={goal} onChange={(event) => setGoal(event.target.value)} maxLength={12000} placeholder="مثلاً می‌خواهم ایمنی آزمایشگاه را یاد بگیرم"/>
                                <button type="button" onClick={() => prepareGoal()} disabled={!goal.trim() || working === 'goal'}>{working === 'goal' ? 'در حال بررسی…' : 'بررسی هدف'}</button>
                            </div>
                            {pendingGoal && <div className="la-goal-result">
                                {pendingGoal.status === 'resolved' && pendingGoal.target ? <>
                                    <p>هدف پیشنهادی: <strong>{pendingGoal.target.concept_name}</strong></p>
                                    <button type="button" className="confirm" onClick={confirmGoal} disabled={working === 'confirm'}><FiCheck/> {working === 'confirm' ? 'در حال ثبت…' : 'تأیید و ساخت مسیر'}</button>
                                </> : <>
                                    <p>{pendingGoal.status === 'ambiguous' ? 'منظورتان کدام مفهوم است؟' : 'مفهوم دقیقی پیدا نشد؛ هدف را دقیق‌تر بنویسید.'}</p>
                                    <div className="la-candidates">{(pendingGoal.candidates || []).map((candidate) => <button type="button" key={candidate.concept_id} onClick={() => prepareGoal(candidate.concept_name)}>{candidate.concept_name}</button>)}</div>
                                </>}
                            </div>}
                        </article>
                    </section>

                    <section className="la-main-grid">
                        <article className="la-panel la-chat-panel">
                            <div className="la-panel-head"><div><FiMessageCircle/><span><b>گفت‌وگو با دستیار</b><small>پاسخ متناسب با سطح علمی شما</small></span></div></div>
                            <div className="la-messages">
                                {!visibleMessages.length && <div className="la-chat-empty"><FiMessageCircle/><p>درباره سطح، مسیر مطالعه یا تمرین بعدی سؤال کنید.</p></div>}
                                {visibleMessages.map((item) => <div key={item.key} className={`la-message ${item.role}`}><span>{item.role === 'user' ? 'شما' : 'دستیار'}</span><p><MathText text={item.text}/></p></div>)}
                                {sending && <div className="la-message assistant pending"><span>دستیار</span><p><i/><i/><i/></p></div>}
                                <div ref={chatEndRef}/>
                            </div>
                            <form className="la-chat-form" onSubmit={sendMessage}>
                                <textarea aria-label="پیام به دستیار یادگیری" value={message} onChange={(event) => setMessage(event.target.value)} maxLength={12000} rows="2" placeholder="پیام خود را بنویسید…" onKeyDown={(event) => {
                                    if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); event.currentTarget.form.requestSubmit(); }
                                }}/>
                                <button type="submit" disabled={!message.trim() || sending} aria-label="ارسال"><FiSend/></button>
                            </form>
                        </article>

                        <aside className="la-panel la-mastery-panel">
                            <div className="la-panel-head"><div><FiTrendingUp/><span><b>مفاهیم درس</b><small>{mastery.length} مفهوم</small></span></div></div>
                            <div className="la-concepts">{mastery.map((concept) => <button type="button" key={concept.concept_id} className="la-concept" onClick={() => showMaterial(concept.concept_id)} disabled={working === `material-${concept.concept_id}`}>
                                <span><b>{concept.concept_name}</b><small>{concept.status === 'unassessed' ? 'ارزیابی‌نشده' : `${concept.evidence_count} شاهد یادگیری`}</small></span>
                                <span className="la-concept-score">{percent(concept.mastery)}<FiChevronLeft/></span>
                                <i style={{width: `${(concept.mastery || 0) * 100}%`}}/>
                            </button>)}</div>
                        </aside>
                    </section>

                    {!!learningPath.length && <section className="la-panel la-path-panel"><div className="la-panel-head"><div><FiCompass/><span><b>مسیر پیشنهادی شما</b><small>به‌ترتیب پیش‌نیازها</small></span></div></div><div className="la-path">{learningPath.map((step) => <article key={step.concept_id}><span>{step.order}</span><div><h3>{step.concept_name}</h3><p>{step.description || reasonLabels[step.reason_code]}</p><small>{reasonLabels[step.reason_code] || step.reason_code} · تسلط {percent(step.mastery)}</small></div></article>)}</div></section>}

                    {!!activities.length && <section className="la-panel la-activities"><div className="la-panel-head"><div><FiBookOpen/><span><b>تمرین پیشنهادی</b><small>فعالیت مناسب برای گام بعدی</small></span></div></div>{activities.map((activity) => <article key={activity.question_id}><span>تمرین {activity.recommendation_rank}</span><h3>{activity.prompt}</h3><p>{activity.concept_name} · سطح {activity.difficulty || 'نامشخص'}</p></article>)}</section>}

                    {material?.concept && <div className="la-modal-backdrop" onClick={() => setMaterial(null)}><section className="la-material" role="dialog" aria-modal="true" aria-labelledby="learning-material-title" onClick={(event) => event.stopPropagation()}><button type="button" className="la-modal-close" aria-label="بستن محتوای آموزشی" onClick={() => setMaterial(null)}>×</button><span>محتوای آموزشی</span><h2 id="learning-material-title">{material.concept.name}</h2><p>{material.concept.description || 'برای این مفهوم هنوز توضیحی ثبت نشده است.'}</p>{!!material.concept.learning_objectives?.length && <><h3>هدف‌های یادگیری</h3><ul>{material.concept.learning_objectives.map((item) => <li key={item}>{item}</li>)}</ul></>}{!!material.concept.examples?.length && <><h3>مثال‌ها</h3><ul>{material.concept.examples.map((item) => <li key={item}>{item}</li>)}</ul></>}</section></div>}
                </>}
            </main>
        </div>
    );
};

export default LearningAgent;
